// src/components/SalidaEntradaPatrones/HistorialPatron.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Button,
  Spinner,
  Alert,
  Form,
  Badge,
  InputGroup
} from 'react-bootstrap';
import { FaArrowLeft, FaHistory, FaFilter, FaDownload, FaClock } from 'react-icons/fa';
import { useTable, useSortBy, usePagination } from 'react-table';
import { getPatronHistorial } from '../../api/patronesService';
import { Alertassw } from '../Alertas/Alertassw';
import StatusBadge from './StatusBadge';
import moment from 'moment';
import 'moment/locale/es';
import capitalizeText from '../../utils/capitalizeText';

moment.locale('es');

/**
 * Vista de historial completo de movimientos de un patrón
 */
const HistorialPatron = ({ patron, onBack, onViewMovimiento }) => {
  const [historial, setHistorial] = useState([]);

  console.log('HistorialPatron - patron:', historial);
  const [loading, setLoading] = useState(true);
  const [filtros, setFiltros] = useState({
    startDate: moment().subtract(6, 'months').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    limit: 100
  });
  const [stats, setStats] = useState({
    totalMovimientos: 0,
    promedioDiasCampo: 0,
    totalOtsAtendidas: 0
  });

  useEffect(() => {
    if (patron?._id) {
      cargarHistorial();
    }
  }, [patron, filtros]);

  const cargarHistorial = async () => {
    setLoading(true);
    try {
      const response = await getPatronHistorial(patron._id, filtros);
      const movimientos = response.data || [];
      setHistorial(movimientos);

      // Calcular estadísticas
      calcularEstadisticas(movimientos);
    } catch (error) {
      console.error('Error al cargar historial:', error);
      Alertassw('Error', 'No se pudo cargar el historial del patrón', 'error');
    } finally {
      setLoading(false);
    }
  };

  const calcularEstadisticas = (movimientos) => {
    const totalMovimientos = movimientos.length;
    
    // Calcular promedio de días en campo
    const movimientosCerrados = movimientos.filter(m => m.status === 'closed' && m.endedAt);
    const totalDias = movimientosCerrados.reduce((sum, mov) => {
      const start = moment(mov.startedAt);
      const end = moment(mov.endedAt);
      return sum + end.diff(start, 'days', true);
    }, 0);
    const promedioDiasCampo = movimientosCerrados.length > 0 
      ? (totalDias / movimientosCerrados.length).toFixed(1)
      : 0;

    // Contar OTs atendidas
    const totalOtsAtendidas = movimientos.reduce((sum, mov) => {
      return sum + (mov.otAssignments?.length || 0);
    }, 0);

    setStats({
      totalMovimientos,
      promedioDiasCampo,
      totalOtsAtendidas
    });
  };

  const handleAplicarFiltros = () => {
    cargarHistorial();
  };

  const handleExportar = () => {
    // Implementar exportación a CSV o PDF
    Alertassw('Función en desarrollo', 'La exportación estará disponible próximamente', 'info');
  };

  // Preparar datos para la tabla
  const columns = useMemo(
    () => [
      {
        Header: 'Consecutivo',
        accessor: 'groupConsecutive',
        Cell: ({ value }) => (
          <span className="fw-bold text-primary">{value}</span>
        )
      },
      {
        Header: 'Salida',
        accessor: 'startedAt',
        Cell: ({ value }) => (
          <span>{moment(value).format('DD/MM/YYYY HH:mm')}</span>
        )
      },
      {
        Header: 'Devolución',
        accessor: 'endedAt',
        Cell: ({ value, row }) => {
          if (row.original.status === 'open') {
            return <Badge bg="warning">En curso</Badge>;
          }
          return value ? moment(value).format('DD/MM/YYYY HH:mm') : 'N/A';
        }
      },
      {
        Header: 'Responsable',
        accessor: 'responsibleUserId',
        Cell: ({ value }) => (
          <span>
            {value?.nombre} {value?.apellido}
          </span>
        )
      },
      {
        Header: 'OTs',
        accessor: 'otAssignments',
        Cell: ({ value }) => (
          <Badge bg="info">{value?.length || 0}</Badge>
        )
      },
      {
        Header: 'Duración',
        accessor: row => {
          if (row.status === 'open') {
            return 'En curso';
          }
          if (row.endedAt && row.startedAt) {
            const start = moment(row.startedAt);
            const end = moment(row.endedAt);
            const days = end.diff(start, 'days');
            const hours = end.diff(start, 'hours') % 24;
            if (days > 0) {
              return `${days}d ${hours}h`;
            }
            return `${hours}h`;
          }
          return 'N/A';
        },
        id: 'duracion'
      },
      {
        Header: 'Estado',
        accessor: 'status',
        Cell: ({ value }) => <StatusBadge type="movimiento" status={value} size="sm" />
      },
      {
        Header: 'Acciones',
        Cell: ({ row }) => (
          <Button
            variant="link"
            size="sm"
            onClick={() => onViewMovimiento?.(row.original)}
          >
            Ver detalles
          </Button>
        )
      }
    ],
    [onViewMovimiento]
  );

  const tableInstance = useTable(
    {
      columns,
      data: historial,
      initialState: { pageSize: 10 }
    },
    useSortBy,
    usePagination
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = tableInstance;

  if (!patron) {
    return (
      <Container fluid className="py-4">
        <Alert variant="warning">No se ha seleccionado ningún patrón</Alert>
        <Button variant="outline-secondary" onClick={onBack}>
          <FaArrowLeft className="me-2" />
          Volver
        </Button>
      </Container>
    );
  }

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <Row className="mb-4">
        <Col>
          <div className="d-flex align-items-center gap-3">
            <Button variant="outline-secondary" onClick={onBack}>
              <FaArrowLeft />
            </Button>
            <div className="flex-grow-1">
              <h2 className="text-primary fw-bold mb-1">
                <FaHistory className="me-2" />
                Historial de Movimientos
              </h2>
              <p className="text-muted mb-0">
                {patron.inventario.toUpperCase()} - {capitalizeText(patron.item)}
              </p>
            </div>
          </div>
        </Col>
      </Row>

      {/* Información del patrón */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Body className='w-100'>
          <Row>
            <Col md={8}>
              <h5 className="fw-bold mb-2">{patron.inventario.toUpperCase()}</h5>
              <p className="mb-1">
                <strong>Item:</strong> {capitalizeText(patron.item)}
              </p>
              <p className="mb-1">
                <strong>Marca/Modelo:</strong> {capitalizeText(patron.marca)} {capitalizeText(patron.modelo)}
              </p>
              {patron.serie && (
                <p className="mb-1">
                  <strong>Serie:</strong> {patron.serie}
                </p>
              )}
            </Col>
            <Col md={4} className="text-md-end">
              <StatusBadge
                type="patron"
                status={patron.inService ? 'en-servicio' : 'disponible'}
                size="lg"
              />
              {patron.ubicacion && (
                <div className="mt-2">
                  <small className="text-muted d-block">Ubicación</small>
                  <span>{patron.ubicacion}</span>
                </div>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Estadísticas */}
      <Row className="mb-4">
        <Col md={4}>
          <Card className="shadow-sm h-100 text-center w-100">
            <Card.Body className='w-100'>
              <FaHistory size={40} className="text-primary mb-2" />
              <h6 className="text-muted mb-2">Total Movimientos</h6>
              <h2 className="fw-bold text-primary">{stats.totalMovimientos}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm h-100 text-center w-100">
            <Card.Body className='w-100'>
              <FaClock size={40} className="text-warning mb-2" />
              <h6 className="text-muted mb-2">Promedio Días en Campo</h6>
              <h2 className="fw-bold text-warning">{stats.promedioDiasCampo}</h2>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="shadow-sm h-100 text-center w-100">
            <Card.Body className='w-100'>
              <FaHistory size={40} className="text-success mb-2" />
              <h6 className="text-muted mb-2">OTs Atendidas</h6>
              <h2 className="fw-bold text-success">{stats.totalOtsAtendidas}</h2>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Filtros */}
      <Card className="mb-4 shadow-sm w-100">
        <Card.Header className="bg-light w-100">
          <h5 className="mb-0">
            <FaFilter className="me-2" />
            Filtros
          </h5>
        </Card.Header>
        <Card.Body className='w-100'>
          <Row className="align-items-end">
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Desde</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.startDate}
                  onChange={(e) =>
                    setFiltros({ ...filtros, startDate: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={4}>
              <Form.Group>
                <Form.Label className="fw-bold">Hasta</Form.Label>
                <Form.Control
                  type="date"
                  value={filtros.endDate}
                  onChange={(e) =>
                    setFiltros({ ...filtros, endDate: e.target.value })
                  }
                />
              </Form.Group>
            </Col>
            <Col md={2}>
              <Form.Group>
                <Form.Label className="fw-bold">Límite</Form.Label>
                <Form.Select
                  value={filtros.limit}
                  onChange={(e) =>
                    setFiltros({ ...filtros, limit: parseInt(e.target.value) })
                  }
                >
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                  <option value={200}>200</option>
                  <option value={500}>500</option>
                </Form.Select>
              </Form.Group>
            </Col>
            <Col md={2}>
              <Button
                variant="primary"
                className="w-100"
                onClick={handleAplicarFiltros}
                disabled={loading}
              >
                {loading ? <Spinner animation="border" size="sm" /> : 'Aplicar'}
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Tabla de historial */}
      <Card className="shadow-sm w-100">
        <Card.Header className="bg-light d-flex justify-content-between align-items-center w-100">
          <h5 className="mb-0">Historial de Movimientos</h5>
          <Button variant="outline-success" size="sm" onClick={handleExportar}>
            <FaDownload className="me-2" />
            Exportar
          </Button>
        </Card.Header>
        <Card.Body className="table-responsive w-100">
          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-3">Cargando historial...</p>
            </div>
          ) : historial.length === 0 ? (
            <Alert variant="info" className="text-center mb-0">
              No hay movimientos registrados para este patrón en el rango seleccionado
            </Alert>
          ) : (
            <>
              <table {...getTableProps()} className="table table-hover mb-0 w-100">
                <thead className="table-light">
                  {headerGroups.map((headerGroup) => (
                    <tr {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => (
                        <th
                          {...column.getHeaderProps(column.getSortByToggleProps())}
                          style={{ cursor: 'pointer' }}
                        >
                          {column.render('Header')}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? ' 🔽'
                                : ' 🔼'
                              : ''}
                          </span>
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody {...getTableBodyProps()}>
                  {page.map((row) => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()}>
                        {row.cells.map((cell) => (
                          <td {...cell.getCellProps()}>{cell.render('Cell')}</td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Paginación */}
              <div className="d-flex justify-content-between align-items-center mt-3">
                <div>
                  Mostrando página {pageIndex + 1} de {pageOptions.length}
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => gotoPage(0)}
                    disabled={!canPreviousPage}
                  >
                    {'<<'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => previousPage()}
                    disabled={!canPreviousPage}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => nextPage()}
                    disabled={!canNextPage}
                  >
                    Siguiente
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => gotoPage(pageCount - 1)}
                    disabled={!canNextPage}
                  >
                    {'>>'}
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default HistorialPatron;
